import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import ConfirmAccount from "./pages/ConfirmAccount";
import Dashboard from "./pages/Dashboard";
import Suppliers from "./pages/Suppliers";
import Invoices from "./pages/Invoices";
import InvoiceDetail from "./pages/InvoiceDetail";
import CreditNotes from "./pages/CreditNotes";
import Validation from "./pages/Validation";
import Workflows from "./pages/Workflows";
import AnalyticalAccounting from "./pages/AnalyticalAccounting";
import Reports from "./pages/Reports";
import Admin from "./pages/Admin";
import SuperAdmin from "./pages/SuperAdmin";
import Subscription from "./pages/Subscription";
import SEPAMandate from "./pages/SEPAMandate";
import BankAccounts from "./pages/BankAccounts";
import SepaTransfers from "./pages/SepaTransfers";
import ScheduledPayments from "./pages/ScheduledPayments";
import AuditLog from "./pages/AuditLog";
import Settings from "./pages/Settings";
import Help from "./pages/Help";
// Vague 1 - Comptabilité
import Roles from "./pages/Roles";
// Vague 2
// Vague 3
import NotesDeFrais from "./pages/NotesDeFrais";
import Tresorerie from "./pages/Tresorerie";
import EspaceExpert from "./pages/EspaceExpert";
import EspaceHolding from "./pages/EspaceHolding";
import Devises from "./pages/Devises";
import InvoiceTemplateEditor from "./pages/InvoiceTemplateEditor";
import Prestations from "./pages/Prestations";
import Conformite from "./pages/Conformite";
import ApiWebhooks from "./pages/ApiWebhooks";
import ConnexionBancaire from "./pages/ConnexionBancaire";
import Lettrage from "./pages/Lettrage";
import CollecteEmail from "./pages/CollecteEmail";
import TVA from "./pages/TVA";
import PlanComptable from "./pages/PlanComptable";
import Ecritures from "./pages/Ecritures";
import GrandLivre from "./pages/GrandLivre";
import Balance from "./pages/Balance";
import Rapprochement from "./pages/Rapprochement";
import Clients from "./pages/Clients";
import FacturesClients from "./pages/FacturesClients";
import EInvoicing from "./pages/EInvoicing";
import Layout from "./components/Layout";

const MODULE_NAMES = {
  'dashboard': 'Tableau de bord',
  'suppliers': 'Gestion des fournisseurs',
  'invoices': 'Factures fournisseurs',
  'validation': 'Workflow de validation',
  'reports': 'Rapports et exports',
  'analytical': 'Comptabilite analytique',
  'budgets': 'Suivi budgetaire',
  'admin': 'Administration'
};

const ProtectedRoute = ({ children, requireSubscription = true, requiredModule = null }) => {
  const { user, loading, needsSubscription, hasModuleAccess } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F5156]"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/" replace />;

  if (requireSubscription && needsSubscription()) {
    return <Navigate to="/subscription" replace />;
  }

  if (requiredModule && user.role !== 'super_admin' && !hasModuleAccess(requiredModule)) {
    const moduleName = MODULE_NAMES[requiredModule] || requiredModule;
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center mb-6 shadow-lg">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Module non disponible</h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 mb-2">
            <span className="font-semibold text-[#0F5156]">{moduleName}</span>
          </p>
          <p className="text-slate-500 dark:text-slate-400 max-w-md mb-8">
            Votre abonnement actuel ne donne pas acces a ce module. Passez a un forfait superieur pour debloquer cette fonctionnalite.
          </p>
          <button onClick={() => window.location.href = '/subscription?action=upgrade'}
            className="px-8 py-3 bg-[#0F5156] text-white font-medium rounded-lg hover:bg-[#0d464a] transition-all shadow-md">
            Mettre a niveau mon abonnement
          </button>
        </div>
      </Layout>
    );
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children, redirectIfAuth = true }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F5156]"></div>
      </div>
    );
  }
  if (user && redirectIfAuth) return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<PublicRoute redirectIfAuth={false}><Landing /></PublicRoute>} />
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/confirm-account" element={<PublicRoute redirectIfAuth={false}><ConfirmAccount /></PublicRoute>} />

          <Route path="/dashboard" element={<ProtectedRoute requiredModule="dashboard"><Dashboard /></ProtectedRoute>} />
          <Route path="/suppliers" element={<ProtectedRoute requiredModule="suppliers"><Suppliers /></ProtectedRoute>} />
          <Route path="/invoices" element={<ProtectedRoute requiredModule="invoices"><Invoices /></ProtectedRoute>} />
          <Route path="/invoices/:id" element={<ProtectedRoute requiredModule="invoices"><InvoiceDetail /></ProtectedRoute>} />
          <Route path="/credit-notes" element={<ProtectedRoute requiredModule="invoices"><CreditNotes /></ProtectedRoute>} />
          <Route path="/validation" element={<ProtectedRoute requiredModule="validation"><Validation /></ProtectedRoute>} />
          <Route path="/workflows" element={<ProtectedRoute requiredModule="validation"><Workflows /></ProtectedRoute>} />
          <Route path="/analytical" element={<ProtectedRoute requiredModule="analytical"><AnalyticalAccounting /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute requiredModule="reports"><Reports /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute requiredModule="admin"><Admin /></ProtectedRoute>} />
          <Route path="/bank-accounts" element={<ProtectedRoute requiredModule="admin"><BankAccounts /></ProtectedRoute>} />
          <Route path="/sepa-transfers" element={<ProtectedRoute requiredModule="invoices"><SepaTransfers /></ProtectedRoute>} />
          <Route path="/scheduled-payments" element={<ProtectedRoute requiredModule="invoices"><ScheduledPayments /></ProtectedRoute>} />
          <Route path="/audit-log" element={<ProtectedRoute requiredModule="admin"><AuditLog /></ProtectedRoute>} />
          {/* Vague 1 - Comptabilité */}
          <Route path="/roles" element={<ProtectedRoute requiredModule="admin"><Roles /></ProtectedRoute>} />
          <Route path="/plan-comptable" element={<ProtectedRoute requiredModule="admin"><PlanComptable /></ProtectedRoute>} />
          <Route path="/ecritures" element={<ProtectedRoute requiredModule="admin"><Ecritures /></ProtectedRoute>} />
          <Route path="/grand-livre" element={<ProtectedRoute requiredModule="admin"><GrandLivre /></ProtectedRoute>} />
          <Route path="/balance" element={<ProtectedRoute requiredModule="admin"><Balance /></ProtectedRoute>} />
          <Route path="/rapprochement" element={<ProtectedRoute requiredModule="admin"><Rapprochement /></ProtectedRoute>} />
          {/* Vague 2 */}
          <Route path="/connexion-bancaire" element={<ProtectedRoute requiredModule="admin"><ConnexionBancaire /></ProtectedRoute>} />
          <Route path="/lettrage" element={<ProtectedRoute requiredModule="admin"><Lettrage /></ProtectedRoute>} />
          <Route path="/collecte-email" element={<ProtectedRoute requiredModule="admin"><CollecteEmail /></ProtectedRoute>} />
          <Route path="/tva" element={<ProtectedRoute requiredModule="admin"><TVA /></ProtectedRoute>} />
          {/* Vague 3 */}
          <Route path="/notes-frais" element={<ProtectedRoute><NotesDeFrais /></ProtectedRoute>} />
          <Route path="/tresorerie" element={<ProtectedRoute requiredModule="admin"><Tresorerie /></ProtectedRoute>} />
          <Route path="/espace-expert" element={<ProtectedRoute><EspaceExpert /></ProtectedRoute>} />
          <Route path="/espace-holding" element={<ProtectedRoute><EspaceHolding /></ProtectedRoute>} />
          <Route path="/devises" element={<ProtectedRoute requiredModule="admin"><Devises /></ProtectedRoute>} />
          <Route path="/invoice-template" element={<ProtectedRoute requiredModule="admin"><InvoiceTemplateEditor /></ProtectedRoute>} />
          <Route path="/prestations" element={<ProtectedRoute requiredModule="invoices"><Prestations /></ProtectedRoute>} />
          <Route path="/conformite" element={<ProtectedRoute requiredModule="admin"><Conformite /></ProtectedRoute>} />
          <Route path="/e-invoicing" element={<ProtectedRoute requiredModule="admin"><EInvoicing /></ProtectedRoute>} />
          <Route path="/api-webhooks" element={<ProtectedRoute requiredModule="admin"><ApiWebhooks /></ProtectedRoute>} />
          <Route path="/clients" element={<ProtectedRoute requiredModule="invoices"><Clients /></ProtectedRoute>} />
          <Route path="/factures-clients" element={<ProtectedRoute requiredModule="invoices"><FacturesClients /></ProtectedRoute>} />
          <Route path="/superadmin" element={<ProtectedRoute><SuperAdmin /></ProtectedRoute>} />
          <Route path="/subscription" element={<ProtectedRoute requireSubscription={false}><Subscription /></ProtectedRoute>} />
          <Route path="/subscription/success" element={<ProtectedRoute requireSubscription={false}><Subscription /></ProtectedRoute>} />
          <Route path="/subscription/sepa" element={<ProtectedRoute requireSubscription={false}><SEPAMandate /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute requireSubscription={false}><Settings /></ProtectedRoute>} />
          <Route path="/help" element={<ProtectedRoute requireSubscription={false}><Help /></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
